import type {
  AuthoredCard,
  Deck,
  TypedValidatorResult,
  ValidatorIssue,
} from "../../../schemas"

const REQUIRED_MERC_COUNT = 24
const MAX_COPIES_PER_CARD = 3
const MIN_LOW_COST_MERCS = 6
const MAX_HIGH_COST_MERCS = 8
const LOW_COST_THRESHOLD = 3
const HIGH_COST_THRESHOLD = 7

export function validateDeck(
  deck: Deck,
  cardMap: ReadonlyMap<string, AuthoredCard>,
): TypedValidatorResult<Deck> {
  const issues: ValidatorIssue[] = []

  // Hero
  if (!deck.heroId) {
    issues.push({
      code: "DECK_MISSING_HERO",
      message: "A deck must include exactly 1 hero.",
      severity: "error",
    })
  }

  // Merc count
  const totalMercs = deck.mercEntries.reduce((sum, e) => sum + e.count, 0)
  if (totalMercs !== REQUIRED_MERC_COUNT) {
    issues.push({
      code: "DECK_INVALID_MERC_COUNT",
      message: `A deck must include exactly ${REQUIRED_MERC_COUNT} authored merc cards. Currently has ${totalMercs}.`,
      path: "mercEntries",
      severity: "error",
    })
  }

  // Per-card copy limit
  for (const entry of deck.mercEntries) {
    if (entry.count > MAX_COPIES_PER_CARD) {
      issues.push({
        code: "DECK_TOO_MANY_COPIES",
        message: `You can include at most ${MAX_COPIES_PER_CARD} copies of a card in a deck. "${entry.cardId}" has ${entry.count}.`,
        path: `mercEntries[${entry.cardId}]`,
        severity: "error",
      })
    }
  }

  // Duplicate card ids
  const seenIds = new Set<string>()
  for (const entry of deck.mercEntries) {
    if (seenIds.has(entry.cardId)) {
      issues.push({
        code: "DECK_DUPLICATE_ENTRY",
        message: `Card "${entry.cardId}" appears more than once in mercEntries.`,
        path: `mercEntries[${entry.cardId}]`,
        severity: "error",
      })
    }
    seenIds.add(entry.cardId)
  }

  // Supply composition rule — needs resolved card costs
  let lowCostCount = 0
  let highCostCount = 0
  let unknownCards = 0

  for (const entry of deck.mercEntries) {
    const card = cardMap.get(entry.cardId)
    if (!card) {
      unknownCards++
      continue
    }
    const cost = card.derivedCost ?? 0
    if (cost <= LOW_COST_THRESHOLD) lowCostCount += entry.count
    if (cost >= HIGH_COST_THRESHOLD) highCostCount += entry.count
  }

  if (unknownCards > 0) {
    issues.push({
      code: "DECK_UNKNOWN_CARDS",
      message: `${unknownCards} card(s) in this deck could not be resolved. Ensure all cards are saved before validating.`,
      severity: "error",
    })
  }

  if (unknownCards === 0) {
    if (lowCostCount < MIN_LOW_COST_MERCS) {
      issues.push({
        code: "DECK_TOO_FEW_LOW_COST",
        message: `At least ${MIN_LOW_COST_MERCS} merc cards must cost ≤${LOW_COST_THRESHOLD}. This deck has ${lowCostCount}.`,
        path: "mercEntries",
        severity: "error",
      })
    }
    if (highCostCount > MAX_HIGH_COST_MERCS) {
      issues.push({
        code: "DECK_TOO_MANY_HIGH_COST",
        message: `At most ${MAX_HIGH_COST_MERCS} merc cards may cost ≥${HIGH_COST_THRESHOLD}. This deck has ${highCostCount}.`,
        path: "mercEntries",
        severity: "error",
      })
    }
  }

  const valid = issues.every((i) => i.severity !== "error")
  return { valid, value: valid ? deck : undefined, issues }
}
